import { defineStore } from "pinia"
import type { User, UserAttendance, UserStatus, UserType } from "~/models/user"
import type { Address } from "~/models/address"
import type { ApiResponse } from "~/types/api"
import type { Role } from "~/models/role"

export type UserStatusType = {
	name: string
	value: string
	borderClass: string
	bgClass: string
	txtClass: string
}

export type UserAuthMode = ReturnType<typeof useUserStore>["authModes"][number]

export const useUserStore = defineStore("user", () => {
	const tokenCookie = useCookie("token", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})
	const refreshTokenCookie = useCookie("refresh_token", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})
	const router = useRouter()
	const routeUrl = useRouteUrl()
	const { $i18n } = useNuxtApp()

	const state = {
		user: ref<User | null>(null),
		addresses: ref<Address[] | null>(null),
		token: ref(tokenCookie.value),
		refreshToken: ref(refreshTokenCookie.value),
		time: ref(0),
		elapsedTime: ref(0),
		types: ref([
			{
				name: $i18n.t("user"),
				value: "user"
			},
			{
				name: $i18n.t("company_user"),
				value: "company-user"
			},
			{
				name: $i18n.t("blogger"),
				value: "blogger"
			},
			{
				name: $i18n.t("support"),
				value: "support"
			},
			{
				name: $i18n.t("developer"),
				value: "developer"
			}
		] as UserStatusType[]),
		statuses: ref([
			{
				name: $i18n.t("enable"),
				value: "enable"
			},
			{
				name: $i18n.t("disable"),
				value: "disable"
			},
			{
				name: $i18n.t("block"),
				value: "block"
			}
		] as UserStatusType[]),
		authModes: ref([
			{
				id: "email",
				title: $i18n.t("email")
			},
			{
				id: "mobile",
				title: $i18n.t("mobile")
			}
		] as const)
	}

	const setToken = (token: string | null) => {
		state.token.value = token
		tokenCookie.value = token
	}

	const setRefreshToken = (refreshToken: string | null) => {
		state.refreshToken.value = refreshToken
		refreshTokenCookie.value = refreshToken
	}

	const requestLogin = async (formData: RequestLoginRequest) => {
		const r = await $fetch<RequestLoginResponse>(RequestLogin.url(), {
			...useFetchConfig(),
			method: RequestLogin.method,
			body: formData
		})
		return r.data
	}

	const secretLogin = async (formData: SecretLoginRequest) => {
		const companyStore = useCompanyStore()
		const r = await $fetch<SecretLoginResponse>(SecretLogin.url(), {
			...useFetchConfig(),
			method: SecretLogin.method,
			body: formData
		})
		if (r.data.status === "enable") {
			state.user.value = r.data
			setToken(r.data.token!)
			setRefreshToken(r.data.refresh_token!)
			companyStore.setCompany(null, null)
		}
		return r.data
	}

	const login = async (formData: LoginRequest) => {
		const companyStore = useCompanyStore()
		const r = await $fetch<LoginResponse>(Login.url(), {
			...useFetchConfig(),
			method: Login.method,
			body: formData
		})
		if (r.data.status === "enable") {
			state.user.value = r.data
			setToken(r.data.token)
			setRefreshToken(r.data.refresh_token)
			companyStore.setCompany(null, null)
		}
		return r.data
	}

	const check = async (formData: CheckRequest) => {
		const r = await $fetch<CheckResponse>(Check.url(), {
			...useFetchConfig(),
			method: Check.method,
			body: formData
		})
		return r.data
	}

	const register = async (formData: RegisterRequest) => {
		const r = await $fetch<RegisterResponse>(Register.url(), {
			...useFetchConfig(),
			method: Register.method,
			body: formData
		})
		return r.data
	}

	const adminLogin = async (formData: AdminLoginRequest) => {
		const companyStore = useCompanyStore()
		const r = await $fetch<AdminLoginResponse>(AdminLogin.url(), {
			...useFetchConfig(),
			method: AdminLogin.method,
			body: formData
		})
		if (r.data.status === "enable") {
			state.user.value = r.data
			setToken(r.data.token)
			setRefreshToken(r.data.refresh_token)
			companyStore.setCompany(null, null)
		}
		return r.data
	}

	const demoLogin = async (formData?: DemoLoginRequest) => {
		const companyStore = useCompanyStore()
		const r = await $fetch<DemoLoginResponse>(DemoLogin.url(), {
			...useFetchConfig(),
			method: DemoLogin.method,
			body: formData
		})
		if (r.data.status === "enable") {
			state.user.value = r.data
			setToken(r.data.token)
			setRefreshToken(r.data.refresh_token)
			companyStore.setCompany(null, null)
		}
		return r.data
	}

	const editProfile = async (formData: EditProfileRequest) => {
		const r = await $fetch<EditProfileResponse>(EditProfile.url(), {
			...useFetchConfig(),
			method: EditProfile.method,
			body: formData
		})
		state.user.value = r.data
		return r.data
	}

	const verifyEmail = async (formData: VerifyEmailRequest) => {
		const r = await $fetch<VerifyEmailResponse>(VerifyEmail.url(), {
			...useFetchConfig(),
			method: VerifyEmail.method,
			body: formData
		})
		return r.data
	}

	const verifyMobile = async (formData: VerifyMobileRequest) => {
		const r = await $fetch<VerifyMobileResponse>(VerifyMobile.url(), {
			...useFetchConfig(),
			method: VerifyMobile.method,
			body: formData
		})
		return r.data
	}

	const verifyForget = async (formData: VerifyForgetRequest) => {
		const r = await $fetch<VerifyForgetResponse>(VerifyForget.url(), {
			...useFetchConfig(),
			method: VerifyForget.method,
			body: formData
		})
		return r.data
	}

	const requestForget = async (formData: ForgetPasswordRequest) => {
		const r = await $fetch<ForgetPasswordResponse>(ForgetPassword.url(), {
			...useFetchConfig(),
			method: ForgetPassword.method,
			body: formData
		})
		return r.data
	}

	const verifyCode = async (formData: VerifyCodeRequest) => {
		const r = await $fetch<VerifyCodeResponse>(VerifyCode.url(), {
			...useFetchConfig(),
			method: VerifyCode.method,
			body: formData
		})
		return r.data
	}

	const changePassword = async (formData: ChangePasswordRequest) => {
		const r = await $fetch<ChangePasswordResponse>(ChangePassword.url(), {
			...useFetchConfig(),
			method: ChangePassword.method,
			body: formData
		})
		return r.data
	}

	const logout = async () => {
		const companyStore = useCompanyStore()
		const type = state.user.value?.type
		const companyId = state.user.value?.company_id
		state.user.value = null
		setToken(null)
		setRefreshToken(null)
		companyStore.setCompany(null, null)
		if (type == "company-user" && companyId) {
			await router.push(routeUrl.CompanyLoginUrl(companyId))
		} else {
			await router.push(routeUrl.HomeUrl())
		}
	}

	const adminLogout = async () => {
		const companyStore = useCompanyStore()
		state.user.value = null
		setToken(null)
		setRefreshToken(null)
		companyStore.setCompany(null, null)
		await router.push(routeUrl.AdminLoginUrl())
	}

	const searchUsers = async (formData: SearchUsersRequest) => {
		const r = await $fetch<SearchUsersResponse>(SearchUsers.url(), {
			...useFetchConfig(),
			method: SearchUsers.method,
			query: formData
		})
		return r.data
	}

	const getUser = async (userId: number, params?: GetUserRequest) => {
		const r = await $fetch<GetUserResponse>(GetUser.url(userId), {
			...useFetchConfig(),
			method: SearchUsers.method,
			params
		})
		return r.data
	}

	const exportUsers = async (formData: ExportUsersRequest) => {
		const data = await $fetch<Blob>(ExportUsers.url(), {
			...useFetchConfig(),
			method: ExportUsers.method,
			body: formData,
			responseType: "blob"
		})
		return data
	}

	const createUserExcel = async (params: CreateUserExcelRequest) => {
		const formData = new FormData()
		Object.keys(params).forEach((k) => formData.set(k, params[k]))
		const r = await $fetch<CreateUserExcelResponse>(CreateUserExcel.url(), {
			...useFetchConfig(),
			method: CreateUserExcel.method,
			body: formData
		})
		return r.data
	}

	const createUser = async (formData: CreateUserRequest) => {
		const r = await $fetch<CreateUserResponse>(CreateUser.url(), {
			...useFetchConfig(),
			method: CreateUser.method,
			body: formData
		})
		return r.data
	}

	const editUser = async (formData: EditUserRequest) => {
		const r = await $fetch<EditUserResponse>(EditUser.url(), {
			...useFetchConfig(),
			method: EditUser.method,
			body: formData
		})
		return r.data
	}

	const deleteUser = async (params: DeleteUserRequest) => {
		const r = await $fetch<DeleteUserResponse>(DeleteUser.url(params.id), {
			...useFetchConfig(),
			method: DeleteUser.method,
			body: params
		})
		return r.data
	}

	const changeUserStatus = async (formData: ChangeUserStatusRequest) => {
		const r = await $fetch<ChangeUserStatusResponse>(ChangeUserStatus.url(), {
			...useFetchConfig(),
			method: ChangeUserStatus.method,
			body: formData
		})
		return r.data
	}

	const changeUserType = async (formData: ChangeUserTypeRequest) => {
		const r = await $fetch<ChangeUserTypeResponse>(ChangeUserType.url(), {
			...useFetchConfig(),
			method: ChangeUserType.method,
			body: formData
		})
		return r.data
	}

	const getCaptcha = async () => {
		const r = await $fetch<GetCaptchaResponse>(GetCaptcha.url(), {
			...useFetchConfig(),
			method: GetCaptcha.method
		})
		return r.data
	}

	const searchAttendances = async (formData: SearchAttendancesRequest) => {
		const r = await $fetch<SearchAttendancesResponse>(SearchAttendances.url(), {
			...useFetchConfig(),
			method: SearchAttendances.method,
			query: formData
		})
		return r.data
	}

	const verifyAttendance = async (formData: VerifyAttendanceRequest) => {
		const r = await $fetch<VerifyAttendanceResponse>(VerifyAttendance.url(), {
			...useFetchConfig(),
			method: VerifyAttendance.method,
			body: formData
		})
		return r.data
	}

	return {
		...state,
		getCaptcha,
		setToken,
		setRefreshToken,
		requestLogin,
		secretLogin,
		login,
		check,
		register,
		adminLogin,
		demoLogin,
		editProfile,
		logout,
		verifyForget,
		adminLogout,
		verifyEmail,
		verifyMobile,
		requestForget,
		verifyCode,
		changePassword,
		searchUsers,
		createUser,
		editUser,
		deleteUser,
		changeUserStatus,
		changeUserType,
		getUser,
		exportUsers,
		createUserExcel,
		searchAttendances,
		verifyAttendance
	}
})

// auth :: request login
export interface RequestLoginRequest {
	company_id?: number
	staff_number: string
}
export interface RequestLoginResponse extends ApiResponse {
	data: User
}
const RequestLogin = {
	method: "POST" as const,
	url: () => "auth/request-login"
}

// auth :: secret login
export interface SecretLoginRequest {
	company_id?: number
	staff_number: string
	code: string
}
export interface SecretLoginResponse extends ApiResponse {
	data: User
}
const SecretLogin = {
	method: "POST" as const,
	url: () => "auth/secret-login"
}

// auth :: login
export interface LoginRequest {
	email?: string
	mobile?: string
	password: string
	captcha_key: string
	captcha: string
	locale?: string
}
export interface LoginResponse extends ApiResponse {
	data: User
}
const Login = {
	method: "POST" as const,
	url: () => "auth/login"
}

// auth :: admin check
export interface CheckRequest {
	email?: string
	mobile?: string
}
export interface CheckResponse extends ApiResponse {
	data: {
		status: UserStatus | null
	}
}
const Check = {
	method: "POST" as const,
	url: () => "auth/check"
}

// auth :: admin login
export interface AdminLoginRequest {
	email: string
	password: string
	captcha_key: string
	captcha: string
}
export interface AdminLoginResponse extends ApiResponse {
	data: User
}
const AdminLogin = {
	method: "POST" as const,
	url: () => "auth/admin-login"
}

// auth :: demo login
export interface DemoLoginRequest {}
export interface DemoLoginResponse extends ApiResponse {
	data: User
}
const DemoLogin = {
	method: "POST" as const,
	url: () => "auth/demo-login"
}

// auth :: register
export interface RegisterRequest {
	email?: string
	mobile?: string
	password: string
	firstname: string
	lastname: string
	longitude: number
	latitude: number
	captcha_key: string
	captcha: string
}
export interface RegisterResponse extends ApiResponse {
	data: User
}
const Register = {
	method: "POST" as const,
	url: () => "auth/register"
}

// auth :: get captcha
export interface GetCaptchaRequest {}
export interface GetCaptchaResponse extends ApiResponse {
	data: {
		text?: string
		image: string
		key: string
	}
}
const GetCaptcha = {
	method: "GET" as const,
	url: () => "auth/captcha"
}

// auth :: forget password
export interface ForgetPasswordRequest {
	email?: string
	mobile?: string
	captcha_key: string
	captcha: string
}
export interface ForgetPasswordResponse extends ApiResponse {
	data: {
		code?: string
	}
}
const ForgetPassword = {
	method: "POST" as const,
	url: () => "auth/forget-password"
}

// auth :: verify email
export interface VerifyEmailRequest {
	email: string
	code: string
}
export interface VerifyEmailResponse extends ApiResponse {
	data: null
}
const VerifyEmail = {
	method: "POST" as const,
	url: () => "auth/verify-email"
}

// auth :: verify mobile
export interface VerifyMobileRequest {
	mobile: string
	code: string
}
export interface VerifyMobileResponse extends ApiResponse {
	data: null
}
const VerifyMobile = {
	method: "POST" as const,
	url: () => "auth/verify-mobile"
}

// auth :: verify forget
export interface VerifyForgetRequest {
	email: string
	code: string
}
export interface VerifyForgetResponse extends ApiResponse {
	data: {
		code?: string
	}
}
const VerifyForget = {
	method: "POST" as const,
	url: () => "auth/verify-forget"
}

// auth :: verify code
export interface VerifyCodeRequest {
	email?: string
	mobile?: string
	code: string
}
export interface VerifyCodeResponse extends ApiResponse {
	data: {
		code: string
	}
}
const VerifyCode = {
	method: "POST" as const,
	url: () => "auth/verify-code"
}

// auth :: change password
export interface ChangePasswordRequest {
	email?: string
	mobile: string
	password: string
	code: string
}
export interface ChangePasswordResponse extends ApiResponse {
	data: null
}
const ChangePassword = {
	method: "POST" as const,
	url: () => "auth/change-password"
}

// auth :: refresh token
export interface RefreshTokenRequest {
	refresh_token: string
}
export interface RefreshTokenResponse extends ApiResponse {
	data: {
		token: string
	}
}
const RefreshToken = {
	method: "POST" as const,
	url: () => "auth/refresh-token"
}

// auth :: logout
const Logout = {
	method: "GET" as const,
	url: () => "auth/logout"
}

// user :: edit profile
export interface EditProfileRequest {
	id?: number
	telephone?: string
	address?: string
	mobile?: string
	firstname?: string
	lastname?: string
	residence_status?: string
	residence_description?: string
	is_land_owner?: string
	is_married?: string
	has_housing_loan?: string
	city_of_residence?: string
}
export interface EditProfileResponse extends ApiResponse {
	data: User
}
const EditProfile = {
	method: "POST" as const,
	url: () => "users/profile"
}

// user :: get profile
export interface GetProfileRequest {}
export interface GetProfileResponse extends ApiResponse {
	data: User
}
const GetProfile = {
	method: "GET" as const,
	url: () => "users/profile"
}

// user :: search users
export interface SearchUsersRequest {
	company_id?: number
	search?: string
	type?: UserType
	role_id?: number
	page?: number
	limit?: number
	organization_unit_id?: number
	order_by?: "created" | "updated"
	direction?: "asc" | "desc"
}
export interface SearchUsersResponse extends ApiResponse {
	data: {
		users: User[]
		total: number
		roles?: Role[]
	}
}
const SearchUsers = {
	method: "GET" as const,
	url: () => "users/search"
}

// user :: export users
export interface ExportUsersRequest {
	company_id?: number
	status?: string
	type?: string
	role_id?: number
}
export type ExportUsersResponse = Blob
const ExportUsers = {
	method: "POST" as const,
	url: () => "users/export"
}

// user :: create user
export interface CreateUserRequest {
	company_id: number | null
	staff_number?: string
	mobile?: string
	email?: string
	firstname?: string
	lastname?: string
	post?: string
	organization_unit?: string
	organization_unit_id?: number | null
	supervisor?: string
	supervisor_id?: number | null
	salon?: string
	account_number?: string
	status?: string
	type?: string
	role_id?: number
	password?: string
}
export interface CreateUserResponse extends ApiResponse {
	data: User
}
const CreateUser = {
	method: "POST" as const,
	url: () => "users/create"
}

// user :: create user excel
export interface CreateUserExcelRequest {
	company_id: number
	file: File
	[key: string]: any
}
export interface CreateUserExcelResponse extends ApiResponse {
	data: {
		added: number
		updated: number
	}
}
const CreateUserExcel = {
	method: "POST" as const,
	url: () => "users/create-excel"
}

// user :: edit user
export interface EditUserRequest {
	id: number
	company_id: number | null
	staff_number?: string
	mobile?: string
	email?: string
	firstname?: string
	lastname?: string
	post?: string
	organization_unit?: string
	organization_unit_id?: number | null
	supervisor?: string
	supervisor_id?: number | null
	salon?: string
	account_number?: string
	status?: string
	type?: string
	role_id?: number
	password?: string
}
export interface EditUserResponse extends ApiResponse {
	data: User
}
const EditUser = {
	method: "POST" as const,
	url: () => "users/edit"
}

// user :: delete user
export interface DeleteUserRequest {
	id: number
	company_id: number | null
}
export interface DeleteUserResponse extends ApiResponse {}
const DeleteUser = {
	method: "POST" as const,
	url: (id: number) => `users/delete/${id}`
}

// user :: change user status
export interface ChangeUserStatusRequest {
	user_id: number
	status: UserStatus
}
export interface ChangeUserStatusResponse extends ApiResponse {
	data: User
}
const ChangeUserStatus = {
	method: "POST" as const,
	url: () => `users/status`
}

// user :: change type type
export interface ChangeUserTypeRequest {
	user_id: number
	type: "user" | "developer"
}
export interface ChangeUserTypeResponse extends ApiResponse {
	data: User
}
const ChangeUserType = {
	method: "POST" as const,
	url: () => `users/type`
}

// user :: get user
export interface GetUserRequest {
	moderation_statuses?: string
	is_moderated?: boolean
}
export interface GetUserResponse extends ApiResponse {
	data: User
}
const GetUser = {
	method: "GET" as const,
	url: (id: number) => `users/${id}`
}

// user :: search attendances
export interface SearchAttendancesRequest {
	company_id: number
	user_id?: number
	limit?: number
	page?: number
	from_date?: string
	to_date?: string
	order_by?: string
	direction?: string
}
export interface SearchAttendancesResponse extends ApiResponse {
	data: {
		attendances: UserAttendance[]
		total: number
		user: User | null
		today_attendance: UserAttendance | null
	}
}
const SearchAttendances = {
	method: "GET" as const,
	url: () => `users/attendances/search`
}

// user :: verify attendance
export interface VerifyAttendanceRequest {
	image_id: number
}
export interface VerifyAttendanceResponse extends ApiResponse {
	data: UserAttendance
}
const VerifyAttendance = {
	method: "POST" as const,
	url: () => `users/attendances/verify`
}

export const useUserApiUrl = () => ({
	Login,
	Check,
	AdminLogin,
	Register,
	GetCaptcha,
	ForgetPassword,
	VerifyEmail,
	VerifyMobile,
	VerifyCode,
	ChangePassword,
	RefreshToken,
	Logout,
	EditProfile,
	GetProfile,
	SearchUsers,
	CreateUser,
	EditUser,
	DeleteUser,
	ChangeUserStatus,
	ChangeUserType,
	GetUser,
	SearchAttendances,
	VerifyAttendance
})
